import { ShallowRef, computed, shallowRef, watch } from 'vue';

const storedValues = new Map<string, ShallowRef<string>>();

export function useLocalStorageString<T extends string>(
  key: string,
  defaultValue: T
): ShallowRef<T> {
  if (storedValues.has(key)) {
    return storedValues.get(key)! as ShallowRef<T>;
  }
  const value = shallowRef<T>((localStorage.getItem(key) as T) ?? defaultValue);
  watch(value, (v) => localStorage.setItem(key, v));
  storedValues.set(key, value);
  return value;
}

export function useLocalStorageNumber<T extends number>(
  key: string,
  defaultValue: T
): ShallowRef<T> {
  const strValue = useLocalStorageString(key, defaultValue.toString());
  return computed({
    get: () => Number(strValue.value) as T,
    set: (v) => (strValue.value = v.toString()),
  });
}

export function useLocalStorageBoolean<T extends boolean>(
  key: string,
  defaultValue: T
): ShallowRef<T> {
  const strValue = useLocalStorageString(key, defaultValue.toString());
  return computed({
    get: () => (strValue.value === 'true') as T,
    set: (v) => (strValue.value = v.toString()),
  });
}

export function useLocalStorageObject<T extends object>(
  key: string,
  defaultValue: T
): ShallowRef<T> {
  const strValue = useLocalStorageString(key, JSON.stringify(defaultValue));
  return computed({
    get: () => JSON.parse(strValue.value) as T,
    set: (v) => (strValue.value = JSON.stringify(v)),
  });
}
